"use client";

import { motion } from "framer-motion";
import { ReactNode } from "react";

interface PageTransitionProps {
  children: ReactNode;
  className?: string;
}

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.98
  },
  in: {
    opacity: 1,
    y: 0,
    scale: 1
  },
  out: {
    opacity: 0,
    y: -20,
    scale: 1.02
  }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.5
};

export default function PageTransition({ children, className = "" }: PageTransitionProps) {
  return (
    <motion.div
      className={`min-h-screen ${className}`}
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      {children}
    </motion.div>
  );
}

// Alternative animation variants for different page types
export const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -30 },
  transition: { duration: 0.3, ease: "easeInOut" }
};

export const slideInRight = {
  initial: { opacity: 0, x: 100 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -100 },
  transition: { duration: 0.4, ease: "easeInOut" }
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 1.1 },
  transition: { duration: 0.3, ease: "easeInOut" }
};

// Custom page wrapper for specific animation types
interface CustomPageTransitionProps extends PageTransitionProps {
  animationType?: "fadeInUp" | "slideInRight" | "scaleIn" | "default";
}

export function CustomPageTransition({ 
  children, 
  className = "", 
  animationType = "default" 
}: CustomPageTransitionProps) {
  const getAnimationProps = () => {
    switch (animationType) {
      case "fadeInUp":
        return fadeInUp;
      case "slideInRight":
        return slideInRight;
      case "scaleIn":
        return scaleIn;
      default:
        return {
          initial: pageVariants.initial,
          animate: pageVariants.in,
          exit: pageVariants.out,
          transition: pageTransition
        };
    }
  };

  const animationProps = getAnimationProps();

  return (
    <motion.div
      className={`min-h-screen ${className}`}
      {...animationProps}
    >
      {children}
    </motion.div>
  );
}